import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  EmploymentByMonth,
  SigningType,
  Worksite,
} from '../../types/Application/application';
import { EmploymentProofType, TypeOfFile } from '../../constants/Enum';
import {
  CsnApplicationFormValues,
  CsnApplicationFullTexts,
  CsnApplicationOrg,
  CsnApplicationOrgEmployment,
} from '../../types/Application/csnApplication';
import { validFileTypes } from '../../fileConfig';
import { ValidatedField } from '../../types/validation';
import { validateField } from '../../validation';
import {
  getEmploymentsByMonth,
  getWorkedMonthsEmploymentCert,
  getWorkedMonthsWageSlips,
} from '../../Functions/Application';
import { CsnApplicationType } from '../../types/Enums/CsnApplicationType';
import { tsl_projektdeltagande_tsl_anstallningsform } from '../../types/Enums/Generated/tsl_projektdeltagande_tsl_anstallningsform';

interface CsnApplicationState {
  applicationType: CsnApplicationType | undefined;
  fullTexts: CsnApplicationFullTexts | undefined;
  form: CsnApplicationFormValues;
  employments: CsnApplicationOrgEmployment;
  employmentsByMonth: EmploymentByMonth[];
  signingType: SigningType | undefined;
  formOfEmployment: tsl_projektdeltagande_tsl_anstallningsform | undefined;
  didNotFindLatestWorksite: boolean;
  isLatestWorksiteSwedishAddress: boolean;
  temporaryEmploymentHasExpired: boolean;
  moreInfo: string;
  isForaApproved: boolean;
}

const initialState: CsnApplicationState = {
  applicationType: undefined,
  fullTexts: undefined,
  form: {
    personalnumber: {
      name: 'personalnumber',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PERSONALNUMBER',
      autocomplete: 'on',
      formPaginationNumber: 1,
    },
    email: {
      name: 'email',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'EMAIL',
      autocomplete: 'email',
      formPaginationNumber: 3,
    },
    telephone: {
      name: 'telephone',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PHONENUMBER',
      autocomplete: 'tel',
      formPaginationNumber: 3,
    },
    latestWorksiteOrgNumber: {
      name: 'latestWorksiteOrgNumber',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'ORGNUMBER',
      autocomplete: 'organization-number',
      formPaginationNumber: 1,
    },
    latestWorksite: {
      name: 'latestWorksite',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'TEXT',
      formPaginationNumber: 1,
    },
    latestWorksiteName: {
      name: 'latestWorksiteName',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'organization',
      formPaginationNumber: 1,
    },
    latestWorksiteAddress: {
      name: 'latestWorksiteAddress',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'address-line1',
      formPaginationNumber: 1,
    },
    latestWorksiteZipCode: {
      name: 'latestWorksiteZipCode',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'ZIPCODE',
      autocomplete: 'postal-code',
      formPaginationNumber: 1,
      isForeignAddress: false,
    },
    latestWorksiteCity: {
      name: 'latestWorksiteCity',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'address-level2 locality',
      formPaginationNumber: 1,
    },
    latestWorksiteEmployedFrom: {
      name: 'latestWorksiteEmployedFrom',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      compareDate: '',
      compareType: 'LESS_THAN',
    },
    latestWorksiteEmployedTo: {
      name: 'latestWorksiteEmployedTo',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      compareDate: '',
      compareType: 'GREATER_THAN',
    },
    latestWorksiteNoticeDate: {
      name: 'latestWorksiteNoticeDate',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      compareDate: '',
      compareType: 'GREATER_THAN',
      compareDateTwo: '',
      compareTypeTwo: 'LESS_THAN',
    },
    latestWorksiteEmploymentFileInput: {
      name: 'latestWorksiteEmploymentFileInput',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'FILE',
      formPaginationNumber: 2,
      files: {
        validFileTypes: validFileTypes,
        files: [],
        typeOfFile: TypeOfFile.EmploymentCertificate,
      },
    },
    latestWorksiteWorkedPeriod: {
      name: 'latestWorksiteWorkedPeriod',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
    },
    addEmploymentOrgNumber: {
      name: 'addEmploymentOrgNumber',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'ORGNUMBER',
      autocomplete: 'organization-number',
      formPaginationNumber: 2,
    },
    addEmploymentEmployedFrom: {
      name: 'addEmploymentEmployedFrom',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      compareDate: '',
      compareType: 'LESS_THAN',
    },
    addEmploymentEmployedTo: {
      name: 'addEmploymentEmployedTo',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      compareDate: '',
      compareType: 'GREATER_THAN',
    },
    addEmploymentFileInput: {
      name: 'addEmploymentFileInput',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'FILE',
      formPaginationNumber: 2,
      files: {
        validFileTypes: validFileTypes,
        files: [],
        typeOfFile: TypeOfFile.EmploymentCertificate,
      },
    },
    addEmploymentWorkedPeriod: {
      name: 'addEmploymentWorkedPeriod',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
    },
  },
  employments: {},
  employmentsByMonth: [],
  signingType: undefined,
  formOfEmployment:
    tsl_projektdeltagande_tsl_anstallningsform.Tillsvidareanstlld,
  isForaApproved: false,
  didNotFindLatestWorksite: false,
  isLatestWorksiteSwedishAddress: true,
  temporaryEmploymentHasExpired: false,
  moreInfo: '',
};

const csnApplicationSlice = createSlice({
  name: 'csnApplication',
  initialState: initialState,
  reducers: {
    setStateToInitialState: (state) => {
      return { ...initialState };
    },
    setFullTextsCsnApplication: (
      state,
      action: PayloadAction<CsnApplicationFullTexts>
    ) => {
      return { ...state, fullTexts: action.payload };
    },
    validateAndSetFormValueCsnApplication: (
      state,
      action: PayloadAction<ValidatedField>
    ) => {
      if (action.payload.validationType === 'FILE') {
        const { files, ...payloadWithoutFiles } = action.payload;
        state.form = {
          ...state.form,
          [action.payload.name]: {
            ...state.form[
              action.payload.name as keyof CsnApplicationFormValues
            ],
            payloadWithoutFiles,
            isValid: validateField({
              ...state.form[
                action.payload.name as keyof CsnApplicationFormValues
              ],
              ...action.payload,
            }),
          },
        };
      } else {
        state.form = {
          ...state.form,
          [action.payload.name]: {
            ...state.form[
              action.payload.name as keyof CsnApplicationFormValues
            ],
            ...action.payload,
            isValid: validateField({
              ...state.form[
                action.payload.name as keyof CsnApplicationFormValues
              ],
              ...action.payload,
            }),
          },
        };
      }
    },
    setApplicationType: (state, action: PayloadAction<CsnApplicationType>) => {
      return { ...state, applicationType: action.payload };
    },
    setFormOfEmployment: (
      state,
      action: PayloadAction<tsl_projektdeltagande_tsl_anstallningsform>
    ) => {
      return { ...state, formOfEmployment: action.payload };
    },

    setWorksiteLatestEmployment: (
      state,
      action: PayloadAction<{ didNotFindWorksite: boolean; worksite: Worksite }>
    ) => {
      const latestEmployment = Object.values(state.employments).find(
        (emp: CsnApplicationOrg) => emp.isLatestEmployment
      );
      if (latestEmployment === undefined) {
        const nodeId = Date.now().toString();
        const newLatestEmployment = {
          nodeId: nodeId,
          isLatestEmployment: true,
          employmentProofType: EmploymentProofType.EmploymentCertification,
          ...action.payload.worksite,
        } as CsnApplicationOrg;
        state.employments[nodeId] = newLatestEmployment;
        if (action.payload.didNotFindWorksite) {
          state.form.latestWorksite.isValid = true;
          state.didNotFindLatestWorksite = true;
        } else {
          state.didNotFindLatestWorksite = false;
          state.form.latestWorksiteName.isValid = true;
          state.form.latestWorksiteAddress.isValid = true;
          state.form.latestWorksiteZipCode.isValid = true;
          state.form.latestWorksiteCity.isValid = true;
        }
      } else {
        state.employments[latestEmployment.nodeId] = {
          ...state.employments[latestEmployment.nodeId],
          ...action.payload.worksite,
        } as CsnApplicationOrg;
        if (action.payload.didNotFindWorksite) {
          state.form.latestWorksite.isValid = true;
          state.didNotFindLatestWorksite = true;
        } else {
          state.didNotFindLatestWorksite = false;
          state.form.latestWorksiteName.isValid = true;
          state.form.latestWorksiteAddress.isValid = true;
          state.form.latestWorksiteZipCode.isValid = true;
          state.form.latestWorksiteCity.isValid = true;
        }
      }
    },
    setIsForaApprovedCsnApplication: (
      state,
      action: PayloadAction<boolean>
    ) => {
      return { ...state, isForaApproved: action.payload };
    },
    setIsSwedishAddressLatestEmployment: (
      state,
      action: PayloadAction<boolean>
    ) => {
      return { ...state, isLatestWorksiteSwedishAddress: action.payload };
    },
    setTemporaryEmploymentHasExpired: (
      state,
      action: PayloadAction<boolean>
    ) => {
      return { ...state, temporaryEmploymentHasExpired: action.payload };
    },
    addEmployment: (state, action: PayloadAction<CsnApplicationOrg>) => {
      if (action.payload.wageSlip !== undefined) {
        state.employments[action.payload.nodeId] = {
          ...action.payload,
          months: getWorkedMonthsWageSlips(
            action.payload.wageSlip,
            action.payload.employedFrom!,
            action.payload.employedTo!
          ),
        };
      }
      if (action.payload.employmentCert !== undefined) {
        state.employments[action.payload.nodeId] = {
          ...action.payload,
          months: getWorkedMonthsEmploymentCert(
            action.payload.employedFrom!,
            action.payload.employedTo!
          ),
        };
      }
    },
    saveEditedEmploymentCsnApplication: (
      state,
      action: PayloadAction<CsnApplicationOrg>
    ) => {
      if (state.employments[action.payload.nodeId] !== undefined) {
        if (action.payload.wageSlip !== undefined) {
          const months = getWorkedMonthsWageSlips(
            action.payload.wageSlip,
            action.payload.employedFrom!,
            action.payload.employedTo!
          );
          state.employments[action.payload.nodeId] = {
            ...action.payload,
            months,
          };
        } else if (action.payload.employmentCert !== undefined) {
          const months = getWorkedMonthsEmploymentCert(
            action.payload.employedFrom!,
            action.payload.employedTo!
          );
          state.employments[action.payload.nodeId] = {
            ...action.payload,
            months,
          };
        } else {
          state.employments[action.payload.nodeId] = {
            ...action.payload,
          };
        }
      }
    },
    deleteEmploymentCsnApplication: (state, action: PayloadAction<string>) => {
      delete state.employments[action.payload];
    },
    clearLatestEmploymentWorksiteFields: (state) => {
      const {
        latestWorksiteName,
        latestWorksiteAddress,
        latestWorksiteZipCode,
        latestWorksiteCity,
      } = state.form;

      const fieldNames: string[] = [
        latestWorksiteName.name,
        latestWorksiteAddress.name,
        latestWorksiteZipCode.name,
        latestWorksiteCity.name,
      ];

      fieldNames.forEach((fieldName) => {
        state.form = {
          ...state.form,
          [fieldName]: {
            ...initialState.form[fieldName as keyof CsnApplicationFormValues],
          },
        };
      });
      state.didNotFindLatestWorksite = false;
    },
    clearLatestEmploymentFileInputField: (state) => {
      return {
        ...state,
        form: {
          ...state.form,
          latestWorksiteEmploymentFileInput:
            initialState.form.latestWorksiteEmploymentFileInput,
        },
      };
    },
    clearLatestEmploymentWorkedPeriodField: (state) => {
      return {
        ...state,
        form: {
          ...state.form,
          latestWorksiteWorkedPeriod:
            initialState.form.latestWorksiteWorkedPeriod,
        },
      };
    },
    clearAddEmploymentFileInputField: (state) => {
      return {
        ...state,
        form: {
          ...state.form,
          addEmploymentFileInput: initialState.form.addEmploymentFileInput,
        },
      };
    },
    clearAddEmploymentWorkedPeriodField: (state) => {
      return {
        ...state,
        form: {
          ...state.form,
          addEmploymentWorkedPeriod:
            initialState.form.addEmploymentWorkedPeriod,
        },
      };
    },
    clearAddEmploymentFormFields: (state) => {
      const {
        addEmploymentEmployedFrom,
        addEmploymentEmployedTo,
        addEmploymentOrgNumber,
        addEmploymentWorkedPeriod,
        addEmploymentFileInput,
      } = state.form;

      const fieldNames: string[] = [
        addEmploymentEmployedFrom.name,
        addEmploymentEmployedTo.name,
        addEmploymentOrgNumber.name,
        addEmploymentWorkedPeriod.name,
        addEmploymentFileInput.name,
      ];

      fieldNames.forEach((fieldName) => {
        state.form = {
          ...state.form,
          [fieldName]: {
            ...initialState.form[fieldName as keyof CsnApplicationFormValues],
          },
        };
      });
    },
    setEmploymentsByMonthCsnApplication: (state) => {
      const employments = Object.values(state.employments);
      state.employmentsByMonth = getEmploymentsByMonth(employments);
    },
    setMoreInfoCsnApplication: (state, action: PayloadAction<string>) => {
      return { ...state, moreInfo: action.payload };
    },
    setSigningCsnApplication: (state, action: PayloadAction<SigningType>) => {
      return { ...state, signingType: action.payload };
    },
  },
});

export const {
  setStateToInitialState,
  setFullTextsCsnApplication,
  validateAndSetFormValueCsnApplication,
  setApplicationType,
  setFormOfEmployment,
  setWorksiteLatestEmployment,
  setIsForaApprovedCsnApplication,
  setIsSwedishAddressLatestEmployment,
  setTemporaryEmploymentHasExpired,
  addEmployment,
  saveEditedEmploymentCsnApplication,
  deleteEmploymentCsnApplication,
  clearLatestEmploymentWorksiteFields,
  clearLatestEmploymentFileInputField,
  clearLatestEmploymentWorkedPeriodField,
  clearAddEmploymentFileInputField,
  clearAddEmploymentWorkedPeriodField,
  clearAddEmploymentFormFields,
  setEmploymentsByMonthCsnApplication,
  setMoreInfoCsnApplication,
  setSigningCsnApplication,
} = csnApplicationSlice.actions;

export default csnApplicationSlice.reducer;
